"use client";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/store/auth";
import { Button, TopBar } from "./ui";
import { MarketingConsentToggle } from "@/components/MarketingConsentToggle";
import { consentApi } from "@/api/services";

interface EditNameModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  title?: string;
  subtitle?: string;
}

export function EditNameModal({
  open,
  onClose,
  onSuccess,
  title,
  subtitle,
}: EditNameModalProps) {
  const { t } = useTranslation();
  const { user, updateName } = useAuth();
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [marketingOptIn, setMarketingOptIn] = useState(true);

  useEffect(() => {
    if (open) {
      setName(user?.name || "");
      setError(null);
    }
  }, [open, user?.name]);

  if (!open) return null;

  async function handleSave() {
    if (!name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await updateName(name.trim());
      // Best-effort, non-blocking marketing opt-in (source=signup). Runs after
      // the customer record exists so me/consent/marketing can resolve it.
      consentApi.optInMarketing(marketingOptIn).catch((err) =>
        console.warn("[consent] signup opt-in failed (non-fatal)", err),
      );
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(t("v2.signin.err_save_name", "Couldn't save your name. Try again."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 100,
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
      }}
    >
      {/* Backdrop */}
      <div
        onClick={!busy ? onClose : undefined}
        style={{
          position: "absolute",
          inset: 0,
          background: "rgba(0,0,0,0.4)",
          backdropFilter: "blur(4px)",
          WebkitBackdropFilter: "blur(4px)",
        }}
      />

      {/* Sheet */}
      <div
        style={{
          position: "relative",
          width: "100%",
          maxWidth: 500,
          background: "var(--surface-1)",
          borderRadius: "24px 24px 0 0",
          boxShadow: "0 -8px 32px rgba(0,0,0,0.15)",
          padding: "16px 18px calc(env(safe-area-inset-bottom, 0px) + 22px)",
          animation: "sheet-up 300ms cubic-bezier(0.32, 0.72, 0, 1)",
        }}
      >
        <div
          style={{
            width: 36,
            height: 4,
            borderRadius: 999,
            background: "var(--surface-2-strong)",
            margin: "2px auto 16px",
          }}
        />

        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div className="display" style={{ fontSize: 20, fontWeight: 800, color: "var(--ink-1)" }}>
            {title || t("v2.signin.name_title", "What's your name?")}
          </div>
          <div
            style={{
              fontSize: 14,
              color: "var(--ink-2)",
              marginTop: 6,
              lineHeight: 1.4,
            }}
          >
            {subtitle || t("v2.signin.name_subtitle", "We'll use this for your orders.")}
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div>
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "var(--ink-3)",
                textTransform: "uppercase",
                marginBottom: 6,
                paddingLeft: 4,
              }}
            >
              {t("v2.signin.name_label", "Full Name")}
            </div>
            <input
              autoFocus
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("v2.signin.name_placeholder", "Enter your name")}
              style={{
                width: "100%",
                height: 48,
                borderRadius: 12,
                border: "1px solid var(--border)",
                background: "var(--surface-2)",
                padding: "0 14px",
                fontSize: 16,
                color: "var(--ink-1)",
                outline: "none",
                fontFamily: "inherit",
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSave();
              }}
            />
          </div>

          <MarketingConsentToggle value={marketingOptIn} onChange={setMarketingOptIn} />

          {error && (
            <div style={{ color: "var(--danger-600)", fontSize: 13, fontWeight: 600, textAlign: "center" }}>
              {error}
            </div>
          )}

          <Button
            full
            size="lg"
            onClick={handleSave}
            disabled={busy || !name.trim()}
          >
            {busy ? t("v2.signin.saving", "Saving…") : t("v2.signin.save_name", "Continue")}
          </Button>

          <Button
            full
            variant="ghost"
            onClick={onClose}
            disabled={busy}
            style={{ marginTop: 4 }}
          >
            {t("v2.signin.cancel", "Cancel")}
          </Button>
        </div>
      </div>

      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes sheet-up {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
      `}} />
    </div>
  );
}
